/**
 * 結帳頁公開設定 Hook
 * 取得租戶發票／綠界付款方式／物流選項，供結帳頁依此顯示發票區塊與付款／取貨按鈕
 * 使用模組級請求去重：同一 tenantId 在進行中只發一次 checkout-config，避免 Strict Mode 或同頁多處呼叫造成重複請求。
 */

import { useState, useEffect, useCallback } from 'react';
import { getApiBase } from '../api/apiUtils';

export type CheckoutConfig = {
  /** 營業稅率（basis points），0 = 0%，500 = 5%；供購物車／結帳顯示與計算稅金 */
  taxRateBps: number;
  /** 商家封面圖片 URL，供 online_shopping / online_order 頁面頂部顯示 */
  coverImageUrl: string | null;
  linePayEnabled: boolean;
  ecpayPaymentEnabled: boolean;
  ecpayInvoiceEnabled: boolean;
  /** 綠界啟用的付款方式清單（結帳頁僅顯示此清單內按鈕）；空或未設則顯示 ALL */
  ecpayChoosePayment: string[];
  /** 啟用的取貨／物流方式（7-ELEVEN、FamilyMart、OK_MART、HiLife、HOME_DELIVERY）；空或未設則全部顯示 */
  deliveryMethodsEnabled: string[];
  /** 面交預購付款模式：online=線上付款、pickup=面交取貨時付款 */
  preorderPaymentMode: 'online' | 'pickup';
};

type ConfigResult =
  | { config: CheckoutConfig }
  | { config: CheckoutConfig; error: Error };

/** 同一 tenantId 只發一次請求，多個 hook instance 共用同一個 promise */
const checkoutConfigPromiseByTenant = new Map<string, Promise<ConfigResult>>();

async function fetchCheckoutConfigOnce(
  tenantId: string
): Promise<ConfigResult> {
  let promise = checkoutConfigPromiseByTenant.get(tenantId);
  if (promise) return promise;
  promise = (async (): Promise<ConfigResult> => {
    try {
      const base = getApiBase();
      const res = await fetch(
        `${base}/api/tenants/${encodeURIComponent(tenantId)}/checkout-config`,
        { cache: 'no-store' }
      );
      if (!res.ok) {
        throw new Error(`checkout-config: ${res.status}`);
      }
      const json = (await res.json()) as {
        success?: boolean;
        data?: {
          tax_rate_bps?: number;
          cover_image_url?: string | null;
          line_pay_enabled?: boolean;
          ecpay_payment_enabled?: boolean;
          ecpay_invoice_enabled?: boolean;
          ecpay_choose_payment?: string[];
          delivery_methods_enabled?: string[];
          preorder_payment_mode?: 'online' | 'pickup';
        };
      };
      const d = json?.data;
      const taxRateBps =
        typeof d?.tax_rate_bps === 'number' && d.tax_rate_bps >= 0
          ? Math.min(10000, d.tax_rate_bps)
          : 500;
      const coverImageUrl =
        typeof d?.cover_image_url === 'string' &&
        d.cover_image_url.trim() !== ''
          ? d.cover_image_url.trim()
          : null;
      const ecpayChoosePayment = Array.isArray(d?.ecpay_choose_payment)
        ? d.ecpay_choose_payment
        : [];
      const deliveryMethodsEnabled = Array.isArray(d?.delivery_methods_enabled)
        ? d.delivery_methods_enabled
        : [];
      const preorderPaymentMode =
        d?.preorder_payment_mode === 'pickup' ? 'pickup' : 'online';
      return {
        config: {
          taxRateBps,
          coverImageUrl,
          linePayEnabled: !!d?.line_pay_enabled,
          ecpayPaymentEnabled: !!d?.ecpay_payment_enabled,
          ecpayInvoiceEnabled: !!d?.ecpay_invoice_enabled,
          ecpayChoosePayment,
          deliveryMethodsEnabled,
          preorderPaymentMode,
        },
      };
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      return {
        config: {
          taxRateBps: 500,
          coverImageUrl: null,
          linePayEnabled: false,
          ecpayPaymentEnabled: false,
          ecpayInvoiceEnabled: false,
          ecpayChoosePayment: [],
          deliveryMethodsEnabled: [],
          preorderPaymentMode: 'online',
        },
        error: err,
      };
    } finally {
      checkoutConfigPromiseByTenant.delete(tenantId);
    }
  })();
  checkoutConfigPromiseByTenant.set(tenantId, promise);
  return promise;
}

export function useCheckoutConfig(tenantId: string | null): {
  config: CheckoutConfig | null;
  isLoading: boolean;
  error: Error | null;
  mutate: () => Promise<void>;
} {
  const [config, setConfig] = useState<CheckoutConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfig = useCallback(async () => {
    if (!tenantId) {
      setConfig(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    setError(null);
    const result = await fetchCheckoutConfigOnce(tenantId);
    if ('error' in result) {
      setError(result.error);
      setConfig(result.config);
    } else {
      setConfig(result.config);
    }
    setIsLoading(false);
  }, [tenantId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const mutate = useCallback(async () => {
    if (tenantId) checkoutConfigPromiseByTenant.delete(tenantId);
    await fetchConfig();
  }, [tenantId, fetchConfig]);

  return { config, isLoading, error, mutate };
}
